import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import SaveChartButton from "@/components/SaveChartButton";

/**
 * Generic reference-chart layout used by Neurotransmitters, Functional
 * Groups, and Enzyme Kinetics charts. Keeps AminoAcidChart's exact look
 * but takes rows + config as props so we don't duplicate 200 lines of JSX
 * per chart.
 *
 * Props:
 *   - title: chart heading
 *   - subtitle: short tagline under the heading
 *   - sourceChart: stable id for deck save
 *   - rows: [{ key, big, small, name, group, tag, note, essential? }]
 *   - groupColors: { [group]: { bg, text, pill } }
 *   - deckMapper: (row) => {front, back, meta}
 *   - footer: optional footer element (mnemonics, extra notes)
 *   - searchFields: extra field names to search across (defaults to key, name, group)
 */
export default function ReferenceChart({
  title,
  subtitle,
  sourceChart,
  rows,
  groupColors,
  deckMapper,
  footer,
}) {
  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const groups = useMemo(() => Object.keys(groupColors), [groupColors]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (groupFilter !== "all" && r.group !== groupFilter) return false;
      if (!q) return true;
      const hay = `${r.name} ${r.key} ${r.big || ""} ${r.small || ""} ${r.group} ${r.tag || ""} ${r.note || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, groupFilter]);

  return (
    <div className="pm-card p-6 mb-6" data-testid={`chart-${sourceChart}`}>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-display font-bold text-xl">{title}</div>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            {subtitle}
          </p>
        </div>
        <div
          className="text-xs flex items-center gap-2"
          style={{ color: "var(--text-muted)" }}
        >
          <span>{filtered.length}/{rows.length} shown</span>
          <SaveChartButton
            sourceChart={sourceChart}
            title={title}
            cards={rows.map(deckMapper)}
          />
        </div>
      </div>

      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: "var(--text-muted)" }}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="pm-input pl-9 w-full"
            data-testid={`chart-${sourceChart}-search`}
          />
        </div>
        <div
          className="flex flex-wrap gap-2"
          data-testid={`chart-${sourceChart}-group-filter`}
        >
          <button
            onClick={() => setGroupFilter("all")}
            className="rounded-full text-xs font-medium px-3 py-1.5 border"
            style={{
              background: groupFilter === "all" ? "var(--primary)" : "var(--paper)",
              color: groupFilter === "all" ? "white" : "var(--text-secondary)",
              borderColor: groupFilter === "all" ? "var(--primary)" : "var(--border)",
            }}
          >
            All
          </button>
          {groups.map((g) => {
            const active = groupFilter === g;
            const c = groupColors[g];
            return (
              <button
                key={g}
                onClick={() => setGroupFilter(g)}
                className="rounded-full text-xs font-medium px-3 py-1.5 border inline-flex items-center gap-1.5"
                style={{
                  background: active ? c.pill : "var(--paper)",
                  color: active ? "white" : c.text,
                  borderColor: active ? c.pill : "var(--border)",
                }}
              >
                <span
                  className="w-2 h-2 rounded-full inline-block"
                  style={{ background: active ? "white" : c.pill }}
                />
                {g}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2"
        data-testid={`chart-${sourceChart}-grid`}
      >
        {filtered.map((r) => {
          const c = groupColors[r.group] || { bg: "var(--muted)", text: "var(--text)", pill: "var(--primary)" };
          return (
            <div
              key={r.key}
              className="rounded-lg p-3 relative"
              style={{ background: c.bg, borderLeft: `3px solid ${c.pill}` }}
              title={r.note}
              data-testid={`chart-${sourceChart}-row-${r.key}`}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span
                  className="font-display font-bold text-2xl leading-none"
                  style={{ color: c.pill }}
                >
                  {r.big}
                </span>
                {r.small && (
                  <span
                    className="text-[10px] font-mono"
                    style={{ color: c.text }}
                  >
                    {r.small}
                  </span>
                )}
              </div>
              <div
                className="font-semibold text-sm leading-tight flex items-center gap-1"
                style={{ color: c.text }}
              >
                {r.name}
                {r.essential && (
                  <span style={{ color: "#c98a3b" }} title="Key MCAT topic">
                    ★
                  </span>
                )}
              </div>
              {r.tag && (
                <div className="text-[10px] mt-1" style={{ color: c.text, opacity: 0.75 }}>
                  {r.tag}
                </div>
              )}
              {r.note && (
                <div className="text-[10px] mt-1.5 leading-snug" style={{ color: c.text }}>
                  {r.note}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div
          className="text-center py-6 text-sm"
          style={{ color: "var(--text-muted)" }}
        >
          No matches for that filter.
        </div>
      )}

      {footer && (
        <div
          className="mt-5 pt-4 border-t text-xs"
          style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
        >
          {footer}
        </div>
      )}
    </div>
  );
}
